import { useState, FC } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Delete as DeleteIcon,
    Restore as RestoreIcon,
    Send as SendIcon,
} from '@mui/icons-material';
import {
    useUser,
    useDeleteUser,
    useRestoreUser,
    useUpdateUserRole,
    useUpdateUserSubscription,
    useAdjustCredits,
    useAdjustFeathers,
    useChangeUserPassword,
} from '../../hooks/useUser';
import {
    SubscriptionType,
    UserDeletionMode,
    UserDetail,
    UserRole,
} from '../../types/user';
import SendPushDialog from '../push/SendPushDialog';
import UserDeleteDialog from './UserDeleteDialog';
import ActivitySummaryStrip from './sections/ActivitySummaryStrip';
import ManagementPanel from './sections/ManagementPanel';
import StudyTab from './sections/tabs/StudyTab';
import WordbookMasteryTab from './sections/tabs/WordbookMasteryTab';
import MissionTab from './sections/tabs/MissionTab';
import EppiTab from './sections/tabs/EppiTab';
import CurrencyTab from './sections/tabs/CurrencyTab';
import SubscriptionTab from './sections/tabs/SubscriptionTab';
import OpsTab from './sections/tabs/OpsTab';
import { countryFlag, fmtDate, toKstDateTimeInputValue, kstInputToUtcLocalDateTime, ROLE_LABELS, SUBSCRIPTION_TYPE_LABELS } from './userShowUtils';
import { DeviceIcon } from './userShowComponents';

const ACTIVITY_TAB_LABELS = ['학습', '단어장·마스터', '미션', '스티커북', '재화', '구독 이력', '운영/CS'];

// ========== 메인 컴포넌트 ==========
export default function UserShow() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const userId = id ? parseInt(id) : 0;

    const { data: user, isLoading, error, refetch } = useUser(userId);
    const deleteUser = useDeleteUser();
    const restoreUser = useRestoreUser();
    const updateRole = useUpdateUserRole();
    const updateSubscription = useUpdateUserSubscription();
    const adjustCredits = useAdjustCredits();
    const adjustFeathers = useAdjustFeathers();
    const changePassword = useChangeUserPassword();

    // 다이얼로그 상태
    const [roleDialogOpen, setRoleDialogOpen] = useState(false);
    const [newRole, setNewRole] = useState<UserRole>(UserRole.USER);
    const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
    const [newSubscriptionType, setNewSubscriptionType] = useState<SubscriptionType>(SubscriptionType.FREE);
    const [subscriptionExpiresAt, setSubscriptionExpiresAt] = useState('');
    const [pushDialogOpen, setPushDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
    const [restoreSuccessMessage, setRestoreSuccessMessage] = useState<string | null>(null);
    const [creditDialogOpen, setCreditDialogOpen] = useState(false);
    const [creditAmount, setCreditAmount] = useState<string>('');
    const [creditReason, setCreditReason] = useState('');
    const [featherDialogOpen, setFeatherDialogOpen] = useState(false);
    const [featherAmount, setFeatherAmount] = useState<string>('');
    const [featherReason, setFeatherReason] = useState('');
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState<string | null>(null);

    const [activityTab, setActivityTab] = useState(0);

    const handleDelete = (mode: UserDeletionMode) => {
        deleteUser.mutate(
            { id: userId, data: { deletionMode: mode } },
            { onSuccess: () => navigate('/user') },
        );
    };
    const handleRoleChange = () => {
        updateRole.mutate(
            { id: userId, data: { role: newRole } },
            { onSuccess: () => setRoleDialogOpen(false) },
        );
    };
    const handleSubscriptionChange = () => {
        updateSubscription.mutate(
            {
                id: userId,
                data: {
                    subscriptionType: newSubscriptionType,
                    expiresAt: kstInputToUtcLocalDateTime(subscriptionExpiresAt),
                },
            },
            { onSuccess: () => setSubscriptionDialogOpen(false) },
        );
    };
    const handleCreditAdjust = () => {
        const delta = parseInt(creditAmount);
        if (isNaN(delta) || delta === 0) { alert('유효한 크레딧 금액을 입력해주세요.'); return; }
        if (!creditReason.trim()) { alert('조정 사유를 입력해주세요.'); return; }
        adjustCredits.mutate(
            { id: userId, data: { delta, reason: creditReason.trim() } },
            { onSuccess: () => { setCreditDialogOpen(false); setCreditAmount(''); setCreditReason(''); } },
        );
    };
    const handleFeatherAdjust = () => {
        const amount = parseInt(featherAmount);
        if (isNaN(amount) || amount === 0) { alert('유효한 깃털 금액을 입력해주세요.'); return; }
        if (!featherReason.trim()) { alert('조정 사유를 입력해주세요.'); return; }
        if (!window.confirm(`${amount > 0 ? '+' : ''}${amount.toLocaleString()} 깃털을 조정합니다. 운영 재화이므로 신중히 처리해주세요. 계속하시겠습니까?`)) return;
        adjustFeathers.mutate(
            { id: userId, data: { amount, reason: featherReason.trim() } },
            { onSuccess: () => { setFeatherDialogOpen(false); setFeatherAmount(''); setFeatherReason(''); } },
        );
    };
    const handleChangePassword = () => {
        setPasswordError(null);
        if (newPassword.length < 6) { setPasswordError('비밀번호는 최소 6자 이상이어야 합니다.'); return; }
        if (newPassword !== confirmPassword) { setPasswordError('비밀번호가 일치하지 않습니다.'); return; }
        changePassword.mutate(
            { id: userId, data: { newPassword } },
            {
                onSuccess: () => {
                    setPasswordDialogOpen(false);
                    setNewPassword(''); setConfirmPassword('');
                    alert('비밀번호가 변경되었습니다.');
                },
                onError: () => setPasswordError('비밀번호 변경에 실패했습니다.'),
            },
        );
    };

    if (!id || userId <= 0) return <Alert severity="error">잘못된 사용자 ID입니다.</Alert>;
    if (isLoading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    if (error || !user) return <Alert severity="error">사용자를 불러오는데 실패했습니다.</Alert>;

    return (
        <Box p={3}>
            {/* === 1. 헤더 === */}
            <HeaderBar
                user={user}
                onPush={() => setPushDialogOpen(true)}
                onDelete={() => setDeleteDialogOpen(true)}
                onRestore={() => { setRestoreSuccessMessage(null); setRestoreDialogOpen(true); }}
                onBack={() => navigate('/user')}
            />

            {/* === 2. 알림 === */}
            {user.deletedAt && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    이 사용자는 {fmtDate(user.deletedAt)}에 삭제되었습니다.
                </Alert>
            )}
            {restoreSuccessMessage && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRestoreSuccessMessage(null)}>
                    {restoreSuccessMessage}
                </Alert>
            )}
            {user.deletedAt && user.deletionHistory && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        탈퇴 원본 정보 (복원 시 이 정보로 복구됩니다)
                    </Typography>
                    <Typography variant="body2">이메일: {user.deletionHistory.originalEmail}</Typography>
                    <Typography variant="body2">닉네임: {user.deletionHistory.originalNickname || '-'}</Typography>
                    <Typography variant="body2">Firebase UID: {user.deletionHistory.originalFirebaseUid}</Typography>
                    <Typography variant="body2">가입 방식: {user.deletionHistory.provider}</Typography>
                    <Typography variant="body2">삭제 유형: {user.deletionHistory.deletionType === 'CLIENT' ? '사용자 자진 탈퇴' : '관리자 삭제'}</Typography>
                    <Typography variant="body2">삭제 일시: {fmtDate(user.deletionHistory.deletedAt)}</Typography>
                </Alert>
            )}

            {/* === 3. 활동 요약 스트립 === */}
            <ActivitySummaryStrip user={user} />

            {/* === 4. 활동 탭 === */}
            <Paper sx={{ mt: 2 }}>
                <Tabs value={activityTab} onChange={(_, v) => setActivityTab(v)} variant="scrollable" sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                    {ACTIVITY_TAB_LABELS.map((label) => <Tab key={label} label={label} />)}
                </Tabs>
                <Box sx={{ p: 2 }}>
                    {activityTab === 0 && <StudyTab user={user} />}
                    {activityTab === 1 && <WordbookMasteryTab user={user} userId={userId} />}
                    {activityTab === 2 && <MissionTab userId={userId} />}
                    {activityTab === 3 && <EppiTab userId={userId} />}
                    {activityTab === 4 && <CurrencyTab userId={userId} />}
                    {activityTab === 5 && <SubscriptionTab userId={userId} />}
                    {activityTab === 6 && <OpsTab userId={userId} />}
                </Box>
            </Paper>

            {/* === 5. 관리 (접이식) === */}
            <ManagementPanel
                user={user}
                onEditRole={() => { setNewRole(user.role); setRoleDialogOpen(true); }}
                onChangePassword={() => {
                    setNewPassword(''); setConfirmPassword(''); setPasswordError(null);
                    setPasswordDialogOpen(true);
                }}
                onEditSubscription={() => {
                    setNewSubscriptionType(user.subscriptionType);
                    setSubscriptionExpiresAt(
                        user.subscriptionExpiresAt ? toKstDateTimeInputValue(user.subscriptionExpiresAt) : '',
                    );
                    setSubscriptionDialogOpen(true);
                }}
                onAdjustCredits={() => { setCreditAmount(''); setCreditReason(''); setCreditDialogOpen(true); }}
                onAdjustFeathers={() => { setFeatherAmount(''); setFeatherReason(''); setFeatherDialogOpen(true); }}
            />

            {/* === 다이얼로그 === */}
            <UserDeleteDialog
                open={deleteDialogOpen}
                loading={deleteUser.isPending}
                userId={userId}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleDelete}
            />

            <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)}>
                <DialogTitle>역할 변경</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>역할</InputLabel>
                        <Select value={newRole} label="역할" onChange={(e) => setNewRole(e.target.value as UserRole)}>
                            <MenuItem value={UserRole.USER}>사용자</MenuItem>
                            <MenuItem value={UserRole.ADMIN}>관리자</MenuItem>
                            <MenuItem value={UserRole.SUPER_ADMIN}>슈퍼관리자</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRoleDialogOpen(false)}>취소</Button>
                    <Button onClick={handleRoleChange} variant="contained">변경</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={subscriptionDialogOpen} onClose={() => setSubscriptionDialogOpen(false)}>
                <DialogTitle>구독 변경</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>구독 타입</InputLabel>
                        <Select
                            value={newSubscriptionType}
                            label="구독 타입"
                            onChange={(e) => {
                                const type = e.target.value as SubscriptionType;
                                setNewSubscriptionType(type);
                                if (type === SubscriptionType.MONTHLY) {
                                    const d = new Date(); d.setMonth(d.getMonth() + 1);
                                    setSubscriptionExpiresAt(toKstDateTimeInputValue(d.toISOString()));
                                } else if (type === SubscriptionType.YEARLY) {
                                    const d = new Date(); d.setFullYear(d.getFullYear() + 1);
                                    setSubscriptionExpiresAt(toKstDateTimeInputValue(d.toISOString()));
                                } else {
                                    setSubscriptionExpiresAt('');
                                }
                            }}
                        >
                            <MenuItem value={SubscriptionType.FREE}>무료</MenuItem>
                            <MenuItem value={SubscriptionType.MONTHLY}>월간</MenuItem>
                            <MenuItem value={SubscriptionType.YEARLY}>연간</MenuItem>
                        </Select>
                    </FormControl>
                    {newSubscriptionType !== SubscriptionType.FREE && (
                        <TextField
                            fullWidth type="datetime-local" label="만료일"
                            value={subscriptionExpiresAt}
                            onChange={(e) => setSubscriptionExpiresAt(e.target.value)}
                            sx={{ mt: 2 }}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSubscriptionDialogOpen(false)}>취소</Button>
                    <Button onClick={handleSubscriptionChange} variant="contained">변경</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={creditDialogOpen} onClose={() => setCreditDialogOpen(false)}>
                <DialogTitle>크레딧 조정</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        현재 주간 크레딧 잔액: {(user.aiCreditBalance?.balance ?? 0).toLocaleString()}
                    </Typography>
                    <TextField
                        fullWidth type="number" label="크레딧 금액"
                        value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)}
                        placeholder="양수: 지급, 음수: 차감"
                        helperText="양수를 입력하면 지급, 음수를 입력하면 차감됩니다."
                        sx={{ mt: 1 }}
                    />
                    <TextField
                        fullWidth label="조정 사유"
                        value={creditReason} onChange={(e) => setCreditReason(e.target.value)}
                        placeholder="예: 이벤트 보상 지급, 오류 보상 등"
                        sx={{ mt: 2 }} multiline rows={2}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreditDialogOpen(false)}>취소</Button>
                    <Button onClick={handleCreditAdjust} variant="contained" disabled={adjustCredits.isPending}>
                        {adjustCredits.isPending ? '처리 중...' : '조정'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={featherDialogOpen} onClose={() => setFeatherDialogOpen(false)}>
                <DialogTitle>깃털 조정</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>운영 재화 조정입니다. 신중히 처리해주세요.</Alert>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        현재 깃털 잔액: {user.xpBalance.toLocaleString()} (누적 {user.totalXp.toLocaleString()})
                    </Typography>
                    <TextField
                        fullWidth type="number" label="깃털 금액"
                        value={featherAmount} onChange={(e) => setFeatherAmount(e.target.value)}
                        placeholder="양수: 지급, 음수: 차감"
                        helperText="양수를 입력하면 지급, 음수를 입력하면 차감됩니다."
                        sx={{ mt: 1 }}
                    />
                    <TextField
                        fullWidth label="조정 사유"
                        value={featherReason} onChange={(e) => setFeatherReason(e.target.value)}
                        placeholder="예: 이벤트 보상 지급, 오류 보상 등"
                        sx={{ mt: 2 }} multiline rows={2}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFeatherDialogOpen(false)}>취소</Button>
                    <Button onClick={handleFeatherAdjust} variant="contained" color="warning" disabled={adjustFeathers.isPending}>
                        {adjustFeathers.isPending ? '처리 중...' : '조정'}
                    </Button>
                </DialogActions>
            </Dialog>

            <SendPushDialog
                open={pushDialogOpen}
                onClose={() => setPushDialogOpen(false)}
                mode="individual"
                userId={userId}
                userEmail={user.email}
            />

            <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)}>
                <DialogTitle>비밀번호 변경</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {user.email} 사용자의 비밀번호를 변경합니다.
                    </Typography>
                    {passwordError && <Alert severity="error" sx={{ mb: 2 }}>{passwordError}</Alert>}
                    <TextField
                        fullWidth type="password" label="새 비밀번호"
                        value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="최소 6자 이상" sx={{ mt: 1 }}
                    />
                    <TextField
                        fullWidth type="password" label="비밀번호 확인"
                        value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPasswordDialogOpen(false)}>취소</Button>
                    <Button onClick={handleChangePassword} variant="contained" disabled={changePassword.isPending}>
                        {changePassword.isPending ? '처리 중...' : '변경'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>사용자 복원 확인</DialogTitle>
                <DialogContent>
                    {user.deletionHistory ? (
                        <>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                다음 원본 정보로 사용자를 복원합니다:
                            </Typography>
                            <Typography variant="body2">이메일: <strong>{user.deletionHistory.originalEmail}</strong></Typography>
                            <Typography variant="body2">닉네임: <strong>{user.deletionHistory.originalNickname || '-'}</strong></Typography>
                            <Typography variant="body2">Firebase UID: <strong>{user.deletionHistory.originalFirebaseUid}</strong></Typography>
                        </>
                    ) : (
                        <Alert severity="info">
                            탈퇴 이력이 없어 비식별화된 정보 그대로 복원됩니다. (레거시 데이터)
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRestoreDialogOpen(false)}>취소</Button>
                    <Button
                        variant="contained" color="primary"
                        onClick={() => {
                            restoreUser.mutate(userId, {
                                onSuccess: async () => {
                                    setRestoreDialogOpen(false);
                                    setRestoreSuccessMessage('사용자를 복원했습니다.');
                                    await refetch();
                                },
                            });
                        }}
                        disabled={restoreUser.isPending}
                    >복원</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

// ========== Section 1: Header ==========
const HeaderBar: FC<{
    user: UserDetail;
    onPush: () => void;
    onDelete: () => void;
    onRestore: () => void;
    onBack: () => void;
}> = ({ user, onPush, onDelete, onRestore, onBack }) => {
    const flag = countryFlag(user.countryCode);
    return (
        <Box display="flex" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
            <Button startIcon={<ArrowBackIcon />} onClick={onBack}>목록으로</Button>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {user.nickname || user.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">#{user.id}</Typography>
                    <Chip size="small" label={ROLE_LABELS[user.role]} color={user.role === UserRole.USER ? 'default' : 'warning'} />
                    {user.isSubscriptionActive && user.subscriptionType !== SubscriptionType.FREE && (
                        <Chip size="small" label={SUBSCRIPTION_TYPE_LABELS[user.subscriptionType]} color="success" />
                    )}
                    <DeviceIcon deviceType={user.deviceType} />
                    {flag && <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>{flag}</Box>}
                </Box>
                <Typography variant="caption" color="text.secondary">
                    {user.email}
                </Typography>
            </Box>
            <Box display="flex" gap={1}>
                {!user.deletedAt && (
                    <Button variant="contained" color="primary" startIcon={<SendIcon />} onClick={onPush}>
                        푸시
                    </Button>
                )}
                {user.deletedAt ? (
                    <Button variant="contained" color="primary" startIcon={<RestoreIcon />} onClick={onRestore}>복원</Button>
                ) : (
                    <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={onDelete}>삭제</Button>
                )}
            </Box>
        </Box>
    );
};
